import { useEffect, useState } from "react";
import api from "@/lib/api";
import { Card, CardContent } from "@/components/ui/card";
import { Badge } from "@/components/ui/badge";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { Brain, Eye, FileText, History } from "lucide-react";
import DocumentVault, { legalPreviewFile } from "@/components/legal/DocumentVault";
import { FilePreviewDialog } from "@/components/FilePreviewDialog";
import { StatutsVersionsCard } from "@/components/legal/StatutsVersionsCard";

const fmt = (d) => (d ? new Date(d).toLocaleDateString("fr-FR") : "—");

const Knowledge = ({ cards, onView }) => (
  <div className="grid grid-cols-1 md:grid-cols-2 gap-3" data-testid="vault-knowledge">
    {cards.length === 0 && <p className="text-sm text-slate-500">L'IA crée une fiche pour chaque document déposé dans le coffre-fort.</p>}
    {cards.map((c) => (
      <Card key={c.id} data-testid={`knowledge-card-${c.id}`}><CardContent className="p-4 space-y-1.5">
        <div className="flex items-center gap-2"><Badge variant="outline" className="text-[10px]">{c.category}</Badge><span className="text-xs text-slate-500">{fmt(c.date)}</span></div>
        <button type="button" onClick={() => onView(c)} className="text-sm font-medium truncate flex items-center gap-1.5 hover:text-indigo-700 max-w-full" data-testid={`knowledge-view-${c.id}`}>
          <Eye className="w-3.5 h-3.5 shrink-0 text-indigo-600" /><span className="truncate">{c.filename}</span></button>
        <p className="text-sm text-slate-600">{c.summary}</p>
        <div className="flex flex-wrap gap-1">{c.tags.map((t) => <span key={t} className="text-[10px] px-1.5 py-0.5 rounded bg-indigo-50 text-indigo-700">{t}</span>)}</div>
      </CardContent></Card>
    ))}
  </div>
);

const Timeline = ({ events }) => (
  <ol className="relative border-l-2 border-indigo-100 ml-3 space-y-4" data-testid="vault-timeline">
    {events.length === 0 && <p className="text-sm text-slate-500 pl-4">Aucun événement juridique pour l'instant.</p>}
    {events.map((e, i) => (
      <li key={i} className="pl-5 relative" data-testid={`timeline-event-${i}`}>
        <span className="absolute -left-[7px] top-1.5 w-3 h-3 rounded-full bg-indigo-500 border-2 border-white" />
        <p className="font-mono text-xs text-slate-500">{fmt(e.date)} · {e.type}</p>
        <p className="text-sm font-medium">{e.title}</p>
        {e.description && <p className="text-xs text-slate-500 line-clamp-2">{e.description}</p>}
      </li>
    ))}
  </ol>
);

export const LegalVaultHub = ({ refreshKey, onChanged }) => {
  const [data, setData] = useState({ cards: [], events: [] });
  const [sub, setSub] = useState("documents");
  const [preview, setPreview] = useState(null);
  useEffect(() => { if (sub !== "documents") { api.get("/legal/knowledge").then((r) => setData(r.data)).catch(() => {}); } }, [sub, refreshKey]);
  return (
    <Tabs value={sub} onValueChange={setSub} data-testid="vault-hub">
      <TabsList>
        <TabsTrigger value="documents" data-testid="vault-sub-documents"><FileText className="w-3.5 h-3.5 mr-1" />Documents</TabsTrigger>
        <TabsTrigger value="knowledge" data-testid="vault-sub-knowledge"><Brain className="w-3.5 h-3.5 mr-1" />Connaissances</TabsTrigger>
        <TabsTrigger value="timeline" data-testid="vault-sub-timeline"><History className="w-3.5 h-3.5 mr-1" />Chronologie</TabsTrigger>
      </TabsList>
      <TabsContent value="documents" className="space-y-4 mt-4">
        <StatutsVersionsCard refreshKey={refreshKey} onChanged={onChanged} />
        <DocumentVault refreshKey={refreshKey} />
      </TabsContent>
      <TabsContent value="knowledge" className="mt-4"><Knowledge cards={data.cards} onView={setPreview} /></TabsContent>
      <TabsContent value="timeline" className="mt-4"><Timeline events={data.events} /></TabsContent>
      <FilePreviewDialog file={legalPreviewFile(preview)} onClose={() => setPreview(null)} />
    </Tabs>
  );
};
